import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCurrency } from '../../context/CurrencyContext';
import LocalText from '../common/LocalText';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const getItemImage = (item) => {
  if (item.merchandise?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.merchandise.thumbnail.linkLocalizations[0].mediaLink;
  if (item.ticket?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.ticket.thumbnail.linkLocalizations[0].mediaLink;
  if (item.badge?.media?.linkLocalizations?.[0]?.mediaLink)
    return item.badge.media.linkLocalizations[0].mediaLink;
  if (item.avatar?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.avatar.thumbnail.linkLocalizations[0].mediaLink;
  return null;
};

const getItemName = (item) =>
  item.nameLocalizations?.values?.[0]?.value ?? item.name ?? '—';

const getItemDescription = (item) =>
  item.descriptionLocalizations?.values?.[0]?.value ?? item.description ?? '';

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const ItemCard = ({ item, itemTypes = [], onEdit, onDelete, onSuccess, onView, onEditRejected, preview = false, showActions = true }) => {
  const { activeCurrency, currencies } = useCurrency();
  const [viewProposalOpen, setViewProposalOpen] = useState(false);

  const itemCurrencyId   = item.prices?.[0]?.currencyId ?? null;
  const resolvedCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const symbol           = resolvedCurrency?.symbol ?? '';

  const rate      = item.tax?.ratePct ?? 0;
  const inclusive = item.tax?.isInclusive ?? false;

  const baseCostMinor = item.prices?.[0]?.baseCost ?? item.baseCost ?? 0;
  const baseCost      = baseCostMinor / factor;

  const today = new Date().toISOString().slice(0, 10);
  const activeDiscounts = (item.discounts ?? []).filter(d => {
    const start = d.startAt ? String(d.startAt).slice(0, 10) : null;
    const end   = d.endAt   ? String(d.endAt).slice(0, 10)   : null;
    return (!start || today >= start) && (!end || today <= end);
  }).sort((a, b) => (a.discountTypeId ?? 0) - (b.discountTypeId ?? 0));

  let effectiveCost = baseCost;
  for (const d of activeDiscounts) {
    if (d.ratePct != null)
      effectiveCost = effectiveCost * (1 - d.ratePct / 100);
    else if (d.amount != null)
      effectiveCost = Math.max(0, effectiveCost - d.amount / factor);
  }

  const subtotal         = inclusive ? effectiveCost : effectiveCost + effectiveCost * rate / 100;
  const originalSubtotal = inclusive ? baseCost      : baseCost + baseCost * rate / 100;
  const hasDiscount      = activeDiscounts.length > 0 && effectiveCost !== baseCost;

  const isSnapshot    = item._viewType === 'snapshot';
  const isLocked      = item.approvalStatus === 'pending' || item.approvalStatus === 'rejected';
  const canDirectEdit = item.contentStatus === 'draft' && !isLocked;

  const imageUrl  = getItemImage(item);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [imageUrl]);

  const typeLabel   = itemTypes.find(t => t.id === item.itemTypeId)?.Type ?? '—';
  const description = getItemDescription(item);
  const itemName    = getItemName(item);

  const viewApprovalRecord = item.approvalId ? {
    approvalId:       item.approvalId,
    actionType:       item.approvalActionType,
    contentType:      CONTENT_TYPES.ITEM,
    sectionLabel:     'Item',
    contentName:      itemName,
    submittedByName: '',
    submittedAt:      null,
    status:            item.approvalStatus,
  } : null;

  const editTooltip = isLocked
    ? item.approvalStatus === 'pending'
      ? 'A pending proposal exists — View it or Withdraw first'
      : 'A rejected proposal exists — View it or Dismiss first'
    : canDirectEdit ? 'Edit item' : 'Propose edit (requires approval)';

  const deleteTooltip = isLocked
    ? item.approvalStatus === 'pending'
      ? 'A pending proposal exists — Withdraw it first'
      : 'A rejected proposal exists — Dismiss it first'
    : canDirectEdit ? 'Delete item' : 'Propose deletion (requires approval)';

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        ...(!preview && {
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 16px 28px -8px rgba(13,35,83,0.2)',
          },
        }),
      }}
    >
      {/* Snapshot version banner */}
      {isSnapshot && (
        <Box sx={{
          bgcolor: item.approvalStatus === 'pending' ? '#d97706' : '#dc2626',
          color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
        }}>
          {item.approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected'}
        </Box>
      )}

      {/* ── Image ─────────────────────────────────────────── */}
      <Box sx={{ position: 'relative', height: 160, bgcolor: '#e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
        {imageUrl && !imgError ? (
          <Box
            component="img"
            src={imageUrl}
            alt={itemName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <ShoppingBagIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
          </Box>
        )}

        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', p: 1.25 }}>
          <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: 'rgba(59,130,246,0.8)', fontSize: 9, fontWeight: 700, color: 'white', lineHeight: 1.4 }}>
            {typeLabel}
          </Box>
        </Box>
      </Box>

      {/* ── Content ───────────────────────────────────── */}
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography fontWeight={700} fontSize={13.5} sx={{ color: '#1e293b', lineHeight: 1.35, mb: 0.25 }}>
            {itemName}
          </Typography>
          {item.sku && (
            <Typography fontSize={11} sx={{ color: '#64748b', fontFamily: 'monospace', textTransform: 'uppercase' }}>
              {item.sku}
            </Typography>
          )}
        </Box>

        {/* A prize item has no price row at all (item_prices insert is skipped for
            is_price_only items — see ItemRepository.AddAsync) — showing "$0.00" here would
            be meaningless, so the whole price/discount/tax line is skipped for it. */}
        {!item.isPrizeOnly && (
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography fontWeight={800} fontSize={19} sx={{ color: hasDiscount ? '#15803d' : '#1e3a5f', lineHeight: 1 }}>
            {symbol}{subtotal.toFixed(decimalPlaces)}
          </Typography>
          {hasDiscount && (
            <Typography fontSize={12} sx={{ color: '#94a3b8', textDecoration: 'line-through', lineHeight: 1 }}>
              {symbol}{originalSubtotal.toFixed(decimalPlaces)}
            </Typography>
          )}
          {rate > 0 && (
            <Typography fontSize={10} sx={{ color: '#94a3b8' }}>
              {inclusive
                ? <><LocalText>incl.</LocalText> {rate}%</>
                : <>+ {rate}%</>
              } <LocalText>tax</LocalText>
            </Typography>
          )}
        </Box>
        )}

        {description && (
          <Typography
            fontSize={11.5}
            sx={{ color: '#64748b', lineHeight: 1.55, flex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* ── Approval ──────────────────────────────────── */}
      {!preview && (isSnapshot ? item.approvalStatus : item.contentStatus) && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Badge: live cards use contentStatus; snapshot cards use approvalStatus */}
          {(isSnapshot ? item.approvalStatus : item.contentStatus) && (
            <ApprovalStatusBadge
              status={isSnapshot ? item.approvalStatus : item.contentStatus}
              actionType={item.approvalActionType}
            />
          )}

          {/* True draft only (no ca record): Submit for Approval */}
          {!isSnapshot && canDirectEdit && item.contentStatus === 'draft' && showActions && (
            <SubmitForApprovalButton contentType={CONTENT_TYPES.ITEM} contentId={item.id ?? item.itemId} contentName={itemName} onSubmitted={() => onSuccess?.('pending')} />
          )}

          {/* Snapshot: View button */}
          {isSnapshot && viewApprovalRecord && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              onClick={() => setViewProposalOpen(true)}
              sx={viewBtnSx}
            >
              View
            </Button>
          )}
          {/* Snapshot pending: Withdraw */}
          {isSnapshot && item.approvalStatus === 'pending' && item.approvalId && showActions && (
            <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} />
          )}
          {/* Snapshot rejected: Dismiss only */}
          {isSnapshot && item.approvalStatus === 'rejected' && item.approvalId && showActions && (
            <WithdrawButton approvalId={item.approvalId} onWithdrawn={() => onSuccess?.(item.approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
          )}
        </Box>
      )}

      {/* ── Actions ───────────────────────────────────── */}
      {!preview && !isSnapshot && showActions && (
        <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          {isLocked ? (
            <Tooltip title="View live content (read-only)">
              <IconButton
                size="small"
                onClick={() => onView?.(item)}
                sx={{ color: '#3b82f6', borderRadius: '8px', '&:hover': { bgcolor: '#eff6ff', color: '#1d4ed8' } }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={editTooltip}>
              <IconButton
                size="small"
                onClick={() => onEdit(item, !canDirectEdit)}
                sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { bgcolor: '#eff6ff', color: '#1d4ed8' } }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={deleteTooltip}>
            <span>
              <IconButton
                size="small"
                disabled={isLocked}
                onClick={() => onDelete(item, !canDirectEdit)}
                sx={{ color: '#64748b', borderRadius: '8px', '&:hover': { bgcolor: '#fff1f2', color: '#dc2626' } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={showActions && onEditRejected ? (snap) => { setViewProposalOpen(false); onEditRejected(snap, item._liveId ?? item.itemId ?? item.id); } : undefined}
        />
      )}
    </Box>
  );
};

export default ItemCard;
